import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Briefcase, MapPin, Clock, Users, ArrowRight, ArrowLeft,
  Loader2, Send, CheckCircle, Upload, X
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotification } from '../contexts/NotificationContext';
import { supabase } from '../lib/supabase';
import SEO from '../components/SEO';

// Careers Listing Page
export const Careers = () => {
  const { language } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('career_jobs')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading jobs:', error);
        setJobs([]);
      } else {
        setJobs(data || []);
      }
    } catch (err) {
      console.error('Error loading jobs:', err);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const texts = {
    tr: {
      title: 'Kariyer',
      subtitle: 'Geleceği birlikte inşa edelim',
      description: 'Yetenekli ve tutkulu ekip arkadaşları arıyoruz. Teknoloji ve yaratıcılığın kesiştiği noktada, birlikte büyüyelim.',
      openPositions: 'Açık Pozisyonlar',
      noJobs: 'Şu anda açık pozisyon bulunmuyor. Ancak her zaman yetenekli adayları değerlendiriyoruz.',
      applyNow: 'Başvur',
      fullTime: 'Tam Zamanlı',
      partTime: 'Yarı Zamanlı',
      contract: 'Sözleşmeli',
      remote: 'Uzaktan',
      whyJoinUs: 'Neden Biz?',
      benefits: [
        { title: 'Esnek Çalışma', desc: 'Uzaktan veya hibrit çalışma imkanı' },
        { title: 'Sürekli Öğrenme', desc: 'Konferans ve eğitim destekleri' },
        { title: 'Modern Teknolojiler', desc: 'En güncel araçlarla çalışma fırsatı' },
        { title: 'Takım Ruhu', desc: 'Destekleyici ve işbirlikçi ortam' }
      ]
    },
    en: {
      title: 'Careers',
      subtitle: "Let's build the future together",
      description: "We're looking for talented and passionate teammates. Let's grow together where technology and creativity meet.",
      openPositions: 'Open Positions',
      noJobs: 'No open positions at the moment. However, we always evaluate talented candidates.',
      applyNow: 'Apply Now',
      fullTime: 'Full-time',
      partTime: 'Part-time',
      contract: 'Contract',
      remote: 'Remote',
      whyJoinUs: 'Why Join Us?',
      benefits: [
        { title: 'Flexible Working', desc: 'Remote or hybrid work options' },
        { title: 'Continuous Learning', desc: 'Conference and training support' },
        { title: 'Modern Technologies', desc: 'Opportunity to work with the latest tools' },
        { title: 'Team Spirit', desc: 'Supportive and collaborative environment' }
      ]
    }
  };

  const t = texts[language] || texts['tr'];

  const employmentTypeLabels = {
    'full-time': t.fullTime,
    'part-time': t.partTime,
    'contract': t.contract,
    'remote': t.remote
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <SEO
          title={language === 'tr' ? 'Kariyer Fırsatları' : 'Career Opportunities'}
          description={language === 'tr' ? 'Cplus Media LLC ekibine katılın. Açık pozisyonları inceleyin ve başvuru yapın.' : 'Join Cplus Media LLC team. Check open positions and apply.'}
          keywords="kariyer, iş ilanları, yazılım iş ilanları, tasarım iş ilanları, reklam ajansı kariyer"
        />
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title={language === 'tr' ? 'Kariyer Fırsatları' : 'Career Opportunities'}
        description={language === 'tr' ? 'Cplus Media LLC ekibine katılın. Açık pozisyonları inceleyin ve başvuru yapın.' : 'Join Cplus Media LLC team. Check open positions and apply.'}
        keywords="kariyer, iş ilanları, yazılım iş ilanları, tasarım iş ilanları, reklam ajansı kariyer"
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            {t.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-white/90 max-w-3xl mx-auto mb-4"
          >
            {t.subtitle}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/70 max-w-2xl mx-auto"
          >
            {t.description}
          </motion.p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Why Join Us */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            {t.whyJoinUs}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 text-center"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Open Positions */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            {t.openPositions}
          </h2>

          {jobs.length === 0 ? (
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-12 text-center">
              <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">{t.noJobs}</p>
            </div>
          ) : (
            <div className="grid gap-6">
              {jobs.map((job, idx) => {
                const title = language === 'tr' ? job.title_tr : job.title_en;
                const description = language === 'tr' ? job.description_tr : job.description_en;

                return (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Link to={`/careers/${job.slug}`}>
                      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 hover:shadow-lg transition-all hover:-translate-y-1">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                              {title}
                            </h3>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                              {job.department && (
                                <span className="flex items-center gap-1">
                                  <Briefcase className="w-4 h-4" />
                                  {job.department}
                                </span>
                              )}
                              {job.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {job.location}
                                </span>
                              )}
                              {job.employment_type && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {employmentTypeLabels[job.employment_type] || job.employment_type}
                                </span>
                              )}
                            </div>
                            {description && (
                              <p className="text-gray-600 dark:text-gray-400 mt-3 line-clamp-2">
                                {description}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium">
                            <span>{t.applyNow}</span>
                            <ArrowRight className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

// Career Detail Page with Application Form
export const CareerDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { showModal } = useNotification();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    cover_letter: '',
    portfolio_url: '',
    resume_url: ''
  });

  useEffect(() => {
    loadJob();
  }, [slug]);

  const loadJob = async () => {
    try {
      const { data, error } = await supabase
        .from('career_jobs')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        navigate('/careers');
        return;
      }

      setJob(data);
    } catch (err) {
      console.error('Error loading job:', err);
      navigate('/careers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const { error } = await supabase
        .from('career_applications')
        .insert({
          job_id: job.id,
          ...formData
        });

      if (error) throw error;

      showModal(
        language === 'tr' ? 'Başarılı' : 'Success',
        language === 'tr' ? 'Başvurunuz başarıyla iletildi. En kısa sürede sizinle iletişime geçeceğiz.' : 'Your application has been received successfully. We will contact you soon.',
        'success'
      );

      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting application:', err);
      showModal(
        language === 'tr' ? 'Hata' : 'Error',
        language === 'tr' ? 'Başvuru gönderilemedi. Lütfen tekrar deneyin.' : 'Failed to submit application. Please try again.',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const texts = {
    tr: {
      back: 'Tüm Pozisyonlar',
      apply: 'Başvuru Formu',
      fullName: 'Ad Soyad',
      email: 'E-posta',
      phone: 'Telefon',
      coverLetter: 'Ön Yazı',
      portfolioUrl: 'Portfolio URL (Opsiyonel)',
      resumeUrl: 'CV URL (LinkedIn, Drive, vb.)',
      submit: 'Başvuruyu Gönder',
      submitting: 'Gönderiliyor...',
      requirements: 'Gereksinimler',
      responsibilities: 'Sorumluluklar',
      successTitle: 'Başvurunuz Alındı!',
      successMessage: 'Başvurunuz için teşekkür ederiz. En kısa sürede sizinle iletişime geçeceğiz.',
      backToCareers: 'Kariyer Sayfasına Dön'
    },
    en: {
      back: 'All Positions',
      apply: 'Application Form',
      fullName: 'Full Name',
      email: 'Email',
      phone: 'Phone',
      coverLetter: 'Cover Letter',
      portfolioUrl: 'Portfolio URL (Optional)',
      resumeUrl: 'Resume URL (LinkedIn, Drive, etc.)',
      submit: 'Submit Application',
      submitting: 'Submitting...',
      requirements: 'Requirements',
      responsibilities: 'Responsibilities',
      successTitle: 'Application Received!',
      successMessage: 'Thank you for your application. We will contact you as soon as possible.',
      backToCareers: 'Back to Careers'
    }
  };

  const t = texts[language] || texts['tr'];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!job) return null;

  const title = language === 'tr' ? job.title_tr : job.title_en;
  const description = language === 'tr' ? job.description_tr : job.description_en;
  const requirements = language === 'tr' ? job.requirements_tr : job.requirements_en;
  const responsibilities = language === 'tr' ? job.responsibilities_tr : job.responsibilities_en;

  // Parse JSON fields if they're strings
  const reqList = typeof requirements === 'string' ? JSON.parse(requirements || '[]') : (requirements || []);
  const respList = typeof responsibilities === 'string' ? JSON.parse(responsibilities || '[]') : (responsibilities || []);

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-12 text-center max-w-md mx-4"
        >
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {t.successTitle}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            {t.successMessage}
          </p>
          <Link
            to="/careers"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.backToCareers}
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title={`${title} - Kariyer`}
        description={language === 'tr' ? `${title} pozisyonu için başvur. Detayları incele ve ekibimize katıl.` : `Apply for ${title} position. Check details and join our team.`}
        keywords={`kariyer, iş ilanı, ${title.toLowerCase()}, cplus media llc`}
      />

      {/* Hero */}
      <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/careers"
            className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.back}
          </Link>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl font-bold text-white mb-4"
          >
            {title}
          </motion.h1>

          <div className="flex flex-wrap items-center gap-4 text-white/80">
            {job.department && (
              <span className="flex items-center gap-1">
                <Briefcase className="w-4 h-4" />
                {job.department}
              </span>
            )}
            {job.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {job.location}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Job Details */}
          <div className="lg:col-span-2 space-y-8">
            {description && (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{description}</p>
              </div>
            )}

            {respList.length > 0 && (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t.responsibilities}</h3>
                <ul className="space-y-2">
                  {respList.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {reqList.length > 0 && (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t.requirements}</h3>
                <ul className="space-y-2">
                  {reqList.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                      <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Application Form */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 sticky top-24">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">{t.apply}</h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.fullName} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.email} *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.phone}
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.resumeUrl} *
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.resume_url}
                    onChange={(e) => setFormData({ ...formData, resume_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.portfolioUrl}
                  </label>
                  <input
                    type="url"
                    value={formData.portfolio_url}
                    onChange={(e) => setFormData({ ...formData, portfolio_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.coverLetter}
                  </label>
                  <textarea
                    rows={4}
                    value={formData.cover_letter}
                    onChange={(e) => setFormData({ ...formData, cover_letter: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t.submitting}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {t.submit}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Careers;
